import CollapsibleSection from "components/Section";
import { Button } from "components/Button";
import React, { useEffect, useState } from "react";
import TextValue from "components/TitleValue";
import { useTranslation } from "react-i18next";
import {
  TWaterMeter,
  TWaterSubscriber,
  WaterMeterAuthType,
  WaterMeterCommunicationType,
  WaterMeterLocationType,
  WaterMeterModelType,
  WaterMeterType,
} from "types/dto";
import { Controller, useForm } from "react-hook-form";
import { TextField } from "@mui/material";
import { TransparentLoading } from "components/TransparentLoading";
import { Select } from "components/Select";
import RegionAutoComplete from "components/SpecificAutoComplete/RegionAutoComplete";
import { SortObjectByPropName } from "utils/arrayTools";
import { Enum2Array } from "utils/enumTools";

import { Permission } from "components/Permission";

type TProps = {
  data?: TWaterMeter;
  isLoading: boolean;
  onSaveClick: (e: TEditFormTypes) => void;
  isButtonLoading: boolean;
  enableEditing?: boolean;
  onEditClick?: () => void;
};
export type TEditFormTypes = {
  serialNo: string;
  productionYear: number;
  waterMeterType: WaterMeterType;
  locationType: WaterMeterLocationType;
  modelType: WaterMeterModelType;
  provinceId?: string;
  cityId?: string;
  jurisdictionId?: string;
  config: {
    communicationType: WaterMeterCommunicationType;
    authType: WaterMeterAuthType;
    encryptionkey: string;
    // Only for OTTA
    devEUI: string;
    appEUI: string;
    loraAppKey: string;
    // Only for ABP
    loraAppSKey: string;
    loraDeviceAddress: string;
    loraNetworkKey: string;
  };
};

const waterMeterAuthTypeOptions = SortObjectByPropName(
  Enum2Array(WaterMeterAuthType),
  "name"
);
const waterMeterTypeOptions = SortObjectByPropName(
  Enum2Array(WaterMeterType),
  "name"
);
const waterMeterLocationTypeOptions = SortObjectByPropName(
  Enum2Array(WaterMeterLocationType),
  "name"
).filter(
  (item) => String(item.value) !== String(WaterMeterLocationType.Installed)
);
const waterMeterModelTypeOptions = SortObjectByPropName(
  Enum2Array(WaterMeterModelType),
  "name"
);

export const MeterInfo = (props: TProps) => {
  const {
    data,
    isLoading,
    onSaveClick,
    isButtonLoading,
    onEditClick,
    enableEditing,
  } = props;

  const { t } = useTranslation();

  // const [enableEditing, setEnableEditing] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    getValues,
  } = useForm<TEditFormTypes>({
    defaultValues: undefined,
  });

  useEffect(() => {
    if (data) {
      setValue("serialNo", data?.serialNo);
      setValue("productionYear", data?.productionYear);
      setValue("waterMeterType", data?.waterMeterType);
      setValue("locationType", data?.locationType);
      setValue("modelType", data?.modelType);
      setValue("provinceId", data?.provinceId);
      setValue("cityId", data?.cityId);
      setValue("jurisdictionId", data?.jurisdictionId);
      setValue("config.authType", data?.config.authType);
      setValue("config.encryptionkey", data?.config.encryptionkey);
      setValue("config.devEUI", data?.config.devEUI);
      setValue("config.appEUI", data?.config.appEUI);
      setValue("config.loraAppKey", data?.config.loraAppKey);
      setValue("config.loraAppSKey", data?.config.loraAppSKey);
      setValue("config.loraDeviceAddress", data?.config.loraDeviceAddress);
      setValue("config.loraNetworkKey", data?.config.loraNetworkKey);
    }
  }, [data]);

  const renderDifferentConfigByAuthType = () => {
    const isABP = watch("config.authType") === WaterMeterAuthType.ABP;
    if (!isABP) {
      return (
        <div className={"grid grid-cols-3 gap-x-2"}>
          <Controller
            name={"config.devEUI"}
            control={control}
            rules={{
              required: isABP,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  label={t("devEUI")}
                  placeholder={t("devEUI")}
                  size="small"
                  {...field}
                  error={Boolean(error)}
                  className="col-span-1"
                  InputLabelProps={{
                    shrink: !!field.value,
                  }}
                  disabled={!enableEditing}
                />
              );
            }}
          />
          <Controller
            name={"config.appEUI"}
            control={control}
            rules={{
              required: isABP,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  label={t("appEUI")}
                  placeholder={t("appEUI")}
                  size="small"
                  {...field}
                  error={Boolean(error)}
                  className="col-span-1"
                  InputLabelProps={{
                    shrink: !!field.value,
                  }}
                  disabled={!enableEditing}
                />
              );
            }}
          />
          <Controller
            name={"config.loraAppKey"}
            control={control}
            rules={{
              required: isABP,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  label={t("loraAppKey")}
                  placeholder={t("loraAppKey")}
                  size="small"
                  {...field}
                  error={Boolean(error)}
                  className="col-span-1"
                  InputLabelProps={{
                    shrink: !!field.value,
                  }}
                  disabled={!enableEditing}
                />
              );
            }}
          />
        </div>
      );
    } else {
      return (
        <div className={"flex w-full gap-x-2"}>
          <Controller
            name={"config.loraAppSKey"}
            control={control}
            rules={{
              required: !isABP,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  label={t("loraAppSKey")}
                  placeholder={t("loraAppSKey")}
                  size="small"
                  {...field}
                  error={Boolean(error)}
                  className={"flex-1"}
                  InputLabelProps={{
                    shrink: !!field.value,
                  }}
                  disabled={!enableEditing}
                />
              );
            }}
          />
          <Controller
            name={"config.loraDeviceAddress"}
            control={control}
            rules={{
              required: !isABP,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  label={t("loraDeviceAddress")}
                  placeholder={t("loraDeviceAddress")}
                  size="small"
                  {...field}
                  error={Boolean(error)}
                  className={"flex-1"}
                  InputLabelProps={{
                    shrink: !!field.value,
                  }}
                  disabled={!enableEditing}
                />
              );
            }}
          />
          <Controller
            name={"config.loraNetworkKey"}
            control={control}
            rules={{
              required: !isABP,
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <TextField
                  label={t("loraNetworkKey")}
                  placeholder={t("loraNetworkKey")}
                  size="small"
                  {...field}
                  error={Boolean(error)}
                  className={"flex-1"}
                  InputLabelProps={{
                    shrink: !!field.value,
                  }}
                  disabled={!enableEditing}
                />
              );
            }}
          />
        </div>
      );
    }
  };

  return (
    <CollapsibleSection
      title={t("meterInfo")}
      collapsible={false}
      isLoading={isLoading}
      skeleton="text"
      className={"h-full flex flex-col"}
      contentClassName={"flex-1 flex flex-col"}
    >
      <form className="w-full" onSubmit={handleSubmit(onSaveClick)}>
        <div className="flex flex-col w-full gap-y-3">
          <div className={"grid grid-cols-3 gap-x-2"}>
            <Controller
              name={"serialNo"}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <TextField
                    label={t("serialNo")}
                    placeholder={t("serialNo")}
                    size="small"
                    {...field}
                    error={Boolean(error)}
                    className={"col-span-2"}
                    InputLabelProps={{
                      shrink: !!field.value,
                    }}
                    disabled={!enableEditing}
                  />
                );
              }}
            />
            <Controller
              name={"productionYear"}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <TextField
                    label={t("productionYear")}
                    type={"number"}
                    placeholder={t("productionYear")}
                    size="small"
                    {...field}
                    error={Boolean(error)}
                    className={"col-span-1"}
                    InputLabelProps={{
                      shrink: !!field.value,
                    }}
                    disabled={!enableEditing}
                  />
                );
              }}
            />
          </div>
          <div className={"grid grid-cols-3 gap-x-2"}>
            <Controller
              name={"waterMeterType"}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <Select
                    {...field}
                    size="small"
                    onChange={(e) => {
                      field.onChange(e?.value);
                    }}
                    className="col-span-1"
                    label={t("waterMeterType")}
                    options={waterMeterTypeOptions}
                    initialValue={waterMeterTypeOptions.find(
                      (item) =>
                        (item.value as unknown as WaterMeterType) ===
                        field.value
                    )}
                    isError={!!error}
                    disabled={!enableEditing}
                  />
                );
              }}
            />
            <Controller
              name={"locationType"}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <Select
                    {...field}
                    size="small"
                    onChange={(e) => {
                      field.onChange(e?.value);
                    }}
                    className="col-span-1"
                    label={t("waterMeterLocationType")}
                    options={waterMeterLocationTypeOptions}
                    initialValue={waterMeterLocationTypeOptions.find(
                      (item) =>
                        (item.value as unknown as WaterMeterLocationType) ===
                        field.value
                    )}
                    isError={!!error}
                    disabled={!enableEditing}
                  />
                );
              }}
            />
            <Controller
              name={"modelType"}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <Select
                    {...field}
                    size="small"
                    onChange={(e) => {
                      field.onChange(e?.value);
                    }}
                    className="col-span-1"
                    label={t("waterMeterModelType")}
                    options={waterMeterModelTypeOptions}
                    initialValue={waterMeterModelTypeOptions.find(
                      (item) =>
                        (item.value as unknown as WaterMeterModelType) ===
                        field.value
                    )}
                    isError={!!error}
                    disabled={!enableEditing}
                  />
                );
              }}
            />
          </div>
          <div className={"grid grid-cols-3 gap-x-2"}>
            <Controller
              name={"config.authType"}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <Select
                    size="small"
                    className="col-span-1"
                    label={t("waterMeterAuthType")}
                    options={waterMeterAuthTypeOptions}
                    onChange={(e) => {
                      field.onChange(e?.value);
                      if (field.value !== e?.value) {
                        setValue("config.devEUI", "");
                        setValue("config.appEUI", "");
                        setValue("config.loraAppKey", "");
                        setValue("config.loraAppSKey", "");
                        setValue("config.loraDeviceAddress", "");
                        setValue("config.loraNetworkKey", "");
                      }
                    }}
                    // value={field.value}
                    initialValue={waterMeterAuthTypeOptions.find(
                      (item) =>
                        (item.value as unknown as WaterMeterAuthType) ==
                        field.value
                    )}
                    disabled={!enableEditing}
                  />
                );
              }}
            />
            <Controller
              name={"config.encryptionkey"}
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState: { error } }) => {
                return (
                  <TextField
                    label={t("encryptionkey")}
                    placeholder={t("encryptionkey")}
                    size="small"
                    {...field}
                    error={Boolean(error)}
                    className="col-span-2"
                    InputLabelProps={{
                      shrink: !!field.value,
                    }}
                    disabled={!enableEditing}
                  />
                );
              }}
            />
          </div>
          {renderDifferentConfigByAuthType()}
          <RegionAutoComplete
            disabled={!enableEditing}
            defaultValue={{
              provinceId: getValues("provinceId"),
              cityId: getValues("cityId"),
              jurisdictionId: getValues("jurisdictionId"),
            }}
            onChange={(value) => {
              setValue("provinceId", value?.province?.id);
              setValue("cityId", value?.city?.id);
              setValue("jurisdictionId", value?.jurisdiction?.id);
            }}
          />
        </div>
      </form>

      <div className="w-full flex items-center justify-end mt-auto pt-4">
        {enableEditing ? (
          <Button
            variant="contained"
            type={"submit"}
            onClick={() => {
              handleSubmit(onSaveClick)();
            }}
            loading={isButtonLoading}
          >
            {t("save")}
          </Button>
        ) : (
          <Permission allowed={["d_watermeter", "superadmin"]}>
            <Button variant="outlined" onClick={onEditClick}>
              {t("edit")}
            </Button>
          </Permission>
        )}
      </div>
    </CollapsibleSection>
  );
};
